import { useEffect, useState } from "react";
import { addTransfer, updateTransfer } from "./TransactionService";
import { getAllAccounts } from "../accounts/AccountService";
import {
  buildTransferFormInitialValues,
  formatAccountLabel,
  readErrorMessage,
} from "./transactionUtils";

export default function TransferForm({
  initialValues,
  submitLabel = "Transfer",
  title = "Transfer",
  onSaved,
  onCancel,
  replaceTransactionId,
}) {

  const [accounts,setAccounts]=useState([]);
  const [errors, setErrors] = useState({});

  const [form,setForm]=useState(buildTransferFormInitialValues(initialValues));

  useEffect(()=>{ load(); },[]);

  useEffect(() => {
    setForm(buildTransferFormInitialValues(initialValues));
    setErrors({});
  }, [initialValues]);

  const load = async ()=>{
    setAccounts(await getAllAccounts());
  };

  const validate = (nextForm)=>{
    const nextErrors = {};
    if (!nextForm.fromAccountId) nextErrors.fromAccountId = "Select the source account.";
    if (!nextForm.toAccountId) nextErrors.toAccountId = "Select the destination account.";
    if (nextForm.fromAccountId && nextForm.toAccountId && nextForm.fromAccountId === nextForm.toAccountId) {
      nextErrors.toAccountId = "Choose a different destination account.";
    }
    const amount = parseFloat(nextForm.amount);
    if (!nextForm.amount || Number.isNaN(amount) || amount <= 0) nextErrors.amount = "Enter a valid amount.";
    if (!nextForm.date) nextErrors.date = "Select a date.";
    return nextErrors;
  };

  const updateField = (field, value)=>{
    setForm(f=>({ ...f, [field]: value }));
    if (errors[field] || (field === "fromAccountId" && errors.toAccountId)) {
      setErrors(e=>({
        ...e,
        [field]: undefined,
        ...(field === "fromAccountId" ? { toAccountId: undefined } : {})
      }));
    }
  };

  const submit = async (e)=>{
    e.preventDefault();
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    try {
      const res = replaceTransactionId ? await updateTransfer(replaceTransactionId, form) : await addTransfer(form);
      if (!res?.ok) {
        const message = await readErrorMessage(res, "Transfer failed.");
        alert(message);
        return;
      }
      alert(replaceTransactionId ? "Transfer updated" : "Transfer successful");
      if (!replaceTransactionId) {
        setForm(buildTransferFormInitialValues());
      }
      onSaved?.();
    } catch (err) {
      alert(err?.message || "Transfer failed.");
    }
  };

  return (
    <form className="stack-form" onSubmit={submit}>
      <h3>{title}</h3>

      <div className="transaction-field">
        <select
          className={errors.fromAccountId ? "transaction-input-error" : ""}
          value={form.fromAccountId}
          onChange={e=>updateField("fromAccountId", e.target.value)}
          aria-invalid={Boolean(errors.fromAccountId)}
          required
        >
          <option value="">From Account</option>
          {accounts.map(a=><option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>)}
        </select>
        {errors.fromAccountId && <div className="transaction-error-text">{errors.fromAccountId}</div>}
      </div>

      <div className="transaction-field">
        <select
          className={errors.toAccountId ? "transaction-input-error" : ""}
          value={form.toAccountId}
          onChange={e=>updateField("toAccountId", e.target.value)}
          aria-invalid={Boolean(errors.toAccountId)}
          required
        >
          <option value="">To Account</option>
          {accounts.map(a=><option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>)}
        </select>
        {errors.toAccountId && <div className="transaction-error-text">{errors.toAccountId}</div>}
      </div>

      <div className="transaction-field">
        <input
          className={errors.amount ? "transaction-input-error" : ""}
          type="number"
          step="0.01"
          min="0.01"
          inputMode="decimal"
          placeholder="Amount"
          value={form.amount}
          onChange={e=>updateField("amount", e.target.value)}
          aria-invalid={Boolean(errors.amount)}
          required
        />
        {errors.amount && <div className="transaction-error-text">{errors.amount}</div>}
      </div>

      <div className="transaction-field">
        <input
          className={errors.date ? "transaction-input-error" : ""}
          type="date"
          value={form.date}
          onChange={e=>updateField("date", e.target.value)}
          aria-invalid={Boolean(errors.date)}
          required
        />
        {errors.date && <div className="transaction-error-text">{errors.date}</div>}
      </div>

      <div className="transaction-field">
        <textarea placeholder="Description"
          value={form.description}
          onChange={e=>updateField("description", e.target.value)} />
      </div>

      <div className="expense-form-save-wrap">
        {onCancel && (
          <button className="btn" type="button" onClick={onCancel}>Cancel</button>
        )}
        <button className="btn btn-primary" type="submit">{submitLabel}</button>
      </div>
    </form>
  );
}
